import type { ReactNode } from 'react'
import React from 'react'
import { Link } from 'react-router-dom'

import { Button, Dropdown, Icon, Skeleton } from '@fourthwall/components'
import { ChevronDownIcon } from '@fourthwall/icons'

import type { AnalyticsExternalSource } from '../../../../types'
import { calculateValuePercentageChange } from '../../../../utils/calculateValuePercentageChange'
import { AnalyticsChangeIndicator } from '../../../AnalyticsChangeIndicator/AnalyticsChangeIndicator'
import { AnalyticsExternalSourceIcon } from '../../../AnalyticsExternalSourceIcon/AnalyticsExternalSourceIcon'
import { AnalyticsTooltip } from '../../../AnalyticsTooltip/AnalyticsTooltip'
import { AnalyticsTooltipContent } from '../../../AnalyticsTooltipContent/AnalyticsTooltipContent'
import type { AnalyticsCardHeader, AnalyticsCardType, ReportLink } from '../../AnalyticsCard.types'
import * as S from './AnalyticsCardHeading.styled'

export type AnalyticsCardHeadingProps = {
  type: AnalyticsCardType
  externalSource?: AnalyticsExternalSource
  header: AnalyticsCardHeader
  subtitle?: string
  reportLink?: ReportLink
  value?: number | [number, number]
  formatValue?: (value: number) => ReactNode
  isDataLoading?: boolean
  isDefinitionLoading?: boolean
}

const AnalyticsCardHeading = ({
  type,
  header,
  externalSource,
  subtitle,
  value,
  formatValue,
  reportLink,
  isDefinitionLoading,
  isDataLoading,
}: AnalyticsCardHeadingProps) => {
  const displayValue = (value: number) => (formatValue ? formatValue(value) : value)
  const hasDropdown = !!header.options?.length && !!header.onOptionSelect

  if (isDefinitionLoading) {
    return <AnalyticsCardHeadingSkeleton type={type} />
  }

  const renderValue = () => {
    if (
      (isDataLoading && type === 'lineChart') ||
      (isDataLoading && type === 'stats' && !subtitle)
    ) {
      return <Skeleton.Shape width={120} height={36} />
    }

    if (value === undefined) {
      return null
    }

    const hasComparableValue = Array.isArray(value)

    if (hasComparableValue) {
      const changeValue = calculateValuePercentageChange(value)

      return (
        <>
          <S.Value>{displayValue(value[0])}</S.Value>
          <AnalyticsChangeIndicator value={changeValue} showZero={false} />
        </>
      )
    }

    return <S.Value>{displayValue(value)}</S.Value>
  }

  const getReportLinkProps = () => {
    if (!reportLink) return null

    if (typeof reportLink === 'object' && 'to' in reportLink) {
      return { to: reportLink.to, state: reportLink.state }
    }

    return { to: reportLink }
  }

  const reportLinkProps = getReportLinkProps()

  const renderHeaderLabel = () => {
    const label =
      !hasDropdown && reportLinkProps ? (
        <Link {...reportLinkProps}>{header.label}</Link>
      ) : (
        header.label
      )

    if (header.tooltip) {
      return (
        <AnalyticsTooltip content={<AnalyticsTooltipContent {...header.tooltip} />}>
          <S.HeaderLabel underline={!!header.tooltip} isDropdownTrigger={hasDropdown}>
            {label}
          </S.HeaderLabel>
        </AnalyticsTooltip>
      )
    }

    return (
      <S.HeaderLabel underline={!!header.tooltip} isDropdownTrigger={hasDropdown}>
        {label}
      </S.HeaderLabel>
    )
  }

  const renderHeaderLabelContent = () => {
    if (hasDropdown) {
      return (
        <Dropdown
          content={header.options?.map((option) => (
            <Dropdown.Item
              key={option.value}
              label={option.label}
              onClick={() => header.onOptionSelect?.(option.value)}
            />
          ))}
          position="bottom-start"
        >
          <S.DropdownContent>
            {renderHeaderLabel()}
            <S.DropdownButton>
              <Icon component={ChevronDownIcon} height={14} />
            </S.DropdownButton>
          </S.DropdownContent>
        </Dropdown>
      )
    }

    return renderHeaderLabel()
  }

  return (
    <S.Container>
      <S.Content>
        <S.HeaderContainer>
          <S.HeaderLabelWrapper>
            <AnalyticsExternalSourceIcon externalSource={externalSource} />
            {renderHeaderLabelContent()}
          </S.HeaderLabelWrapper>
          <S.Subtitle>{subtitle}</S.Subtitle>
        </S.HeaderContainer>
        {reportLinkProps && (
          <Button appearance="secondary" size="xsmall" label="Full report" {...reportLinkProps} />
        )}
      </S.Content>
      <S.ValueContainer>{renderValue()}</S.ValueContainer>
    </S.Container>
  )
}
export default AnalyticsCardHeading

const AnalyticsCardHeadingSkeleton = ({ type }: { type: AnalyticsCardType }) => {
  return (
    <S.Container>
      <S.Content>
        <S.HeaderContainer>
          <S.HeaderLabelWrapper>
            <Skeleton.Shape width={200} height={24} />
          </S.HeaderLabelWrapper>
          {type === 'stats' && (
            <S.Subtitle>
              <Skeleton.Shape width={140} height={20} />
            </S.Subtitle>
          )}
        </S.HeaderContainer>
        <Skeleton.Shape width={110} height={32} />
      </S.Content>
      <S.ValueContainer>
        {type === 'lineChart' && (
          <S.Value>
            <Skeleton.Shape width={120} height={36} />
          </S.Value>
        )}
      </S.ValueContainer>
    </S.Container>
  )
}
